import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { imgUrl } from "../lib/api";
import { fmtLE, fmtIDR, formatApiError } from "../lib/format";
import { config_summary_client } from "../lib/summary";
import { normalizePhone, validIntlPhone, normalizeEgyptPhone, validEgyptPhone } from "../lib/photoMatch";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { useCustomer } from "../context/CustomerContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Store, Truck, ChevronLeft, Camera, Upload, Trash2, CheckCircle2 } from "lucide-react";

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`min-h-[48px] rounded-xl border px-4 py-2 text-sm font-medium transition-all ${active ? "border-[#8B5A2B] bg-[#8B5A2B] text-white" : "border-[#E5DCC5] bg-white text-[#2C1E16] hover:border-[#8B5A2B]"}`}>{children}</button>
);

export default function Checkout() {
  const { items, productSubtotal, clear, isInvoiceCart, activeInvoice } = useCart();
  const { t } = useLang();
  const { customer, checked: customerChecked, customerLoading } = useCustomer();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [store, setStore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [delivery, setDelivery] = useState({ method: "", zone_id: "" });
  const [cust, setCust] = useState({ name: "", phone: "", phone_number: "", address: "", maps: "", payment: "", notes: "" });
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [disc, setDisc] = useState({ code: "", amount: 0, applied: false });
  const [ref, setRef] = useState({ code: "", amount: 0, applied: false });
  const [pts, setPts] = useState(0);

  // Helper: Save current checkout form state to sessionStorage before navigating to login/register
  const saveDraft = (pendingPromoCode, pendingReferralCode) => {
    try {
      const draft = {
        cust,
        delivery,
        pendingPromo: pendingPromoCode !== undefined ? pendingPromoCode : (disc.code || null),
        pendingReferral: pendingReferralCode !== undefined ? pendingReferralCode : (ref.code || null),
      };
      sessionStorage.setItem("sogil_checkout_draft", JSON.stringify(draft));
    } catch {
      // Ignore storage errors
    }
  };

  const applyReferral = async (overrideCode) => {
    if (validatingReferral) return;
    const codeToApply = (typeof overrideCode === "string" ? overrideCode : ref.code).trim();
    if (!codeToApply) return;

    // If customer authentication is still loading/resolving from token, do not treat as guest
    if (customerLoading) {
      toast.info("Memeriksa status akun, silakan coba sesaat lagi...");
      return;
    }

    if (!customer) {
      toast.info(t("sum.promo_require_auth"));
      saveDraft(disc.code, codeToApply);
      navigate("/akun?returnUrl=/checkout");
      return;
    }

    setValidatingReferral(true);
    try {
      const { data } = await api.post("/referral/validate", { code: codeToApply, subtotal: productSubtotal });
      if (data.valid) {
        setRef({ code: codeToApply, amount: data.discount_amount, applied: true });
        toast.success("Referral diterapkan");
      } else {
        setRef((r) => ({ ...r, amount: 0, applied: false }));
        toast.error(data.message || "Kode referral tidak valid");
        if (data.require_auth) {
          saveDraft(disc.code, codeToApply);
          navigate("/akun?returnUrl=/checkout");
        }
      }
    } catch {
      toast.error("Kode referral tidak valid");
    } finally {
      setValidatingReferral(false);
    }
  };

  const maxPts = customer ? Math.min(customer.points_available || 0, productSubtotal * ((store?.point_redeem_max_pct || 50) / 100)) : 0;
  const usedPts = Math.max(0, Math.min(Number(pts) || 0, maxPts));

  const applyDiscount = async (overrideCode) => {
    if (validatingPromo) return;
    const codeToApply = (typeof overrideCode === "string" ? overrideCode : disc.code).trim();
    if (!codeToApply) return;

    // If customer authentication is still loading/resolving from token, do not treat as guest
    if (customerLoading) {
      toast.info("Memeriksa status akun, silakan coba sesaat lagi...");
      return;
    }

    if (!customer) {
      toast.info(t("sum.promo_require_auth"));
      saveDraft(codeToApply, ref.code);
      navigate("/akun?returnUrl=/checkout");
      return;
    }

    setValidatingPromo(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: codeToApply, subtotal: productSubtotal });
      if (data.valid) {
        setDisc({ code: codeToApply, amount: data.discount_amount, applied: true });
        toast.success(t("sum.discount_ok"));
      } else {
        setDisc((d) => ({ ...d, amount: 0, applied: false }));
        toast.error(data.message || t("sum.discount_bad"));
        if (data.require_auth) {
          saveDraft(codeToApply, ref.code);
          navigate("/akun?returnUrl=/checkout");
        }
      }
    } catch {
      toast.error(t("sum.discount_bad"));
    } finally {
      setValidatingPromo(false);
    }
  };

  const [pendingBenefit, setPendingBenefit] = useState(null);

  useEffect(() => {
    api.get("/delivery-zones").then((r) => setZones(r.data));
    api.get("/store-info").then((r) => setStore(r.data));

    if (isInvoiceCart && activeInvoice?.invoice_id) {
      api.get(`/customer/invoices/${activeInvoice.invoice_id}`).catch((err) => {
        if (err.response?.status === 404) {
          toast.error("Item invoice ini sudah tidak tersedia atau telah dihapus.");
          clear();
          navigate("/keranjang");
        }
      });
    }

    // Restore draft from sessionStorage if returning from /akun
    try {
      const raw = sessionStorage.getItem("sogil_checkout_draft");
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft.cust) setCust((prev) => ({ ...prev, ...draft.cust }));
        if (draft.delivery) setDelivery((prev) => ({ ...prev, ...draft.delivery }));
        if (draft.pendingPromo) {
          setDisc((prev) => ({ ...prev, code: draft.pendingPromo }));
          setPendingBenefit({ type: "promo", code: draft.pendingPromo });
        } else if (draft.pendingReferral) {
          setRef((prev) => ({ ...prev, code: draft.pendingReferral }));
          setPendingBenefit({ type: "referral", code: draft.pendingReferral });
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // When customer authentication is confirmed resolved and productSubtotal is ready, automatically revalidate restored promo/referral
  useEffect(() => {
    if (customerChecked && customer && productSubtotal > 0 && pendingBenefit && !validatingPromo && !validatingReferral) {
      const benefit = pendingBenefit;
      setPendingBenefit(null);
      sessionStorage.removeItem("sogil_checkout_draft");
      if (benefit.type === "promo") {
        applyDiscount(benefit.code);
      } else if (benefit.type === "referral") {
        applyReferral(benefit.code);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerChecked, customer, productSubtotal, pendingBenefit, validatingPromo, validatingReferral]);

  const rate = store?.exchange_rate_idr_per_le || 357;
  const deliveryFee = useMemo(() => {
    if (delivery.method !== "delivery") return 0;
    const z = zones.find((z) => z.id === delivery.zone_id);
    return z ? Number(z.fee_le) : 0;
  }, [delivery, zones]);
  const invoiceDiscount = isInvoiceCart ? (activeInvoice?.invoice_discount_le || 0) : 0;
  const totalLE = Math.max(0, productSubtotal - invoiceDiscount - (ref.applied ? ref.amount : (disc.applied ? disc.amount : 0)) - usedPts + deliveryFee);

  if (!items.length) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="text-[#5C4A3D]">{t("cart.empty")}</p>
        <Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#8B5A2B] px-8">{t("cart.belanja")}</Button>
      </div>
    );
  }

  const handleBuildingPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      return toast.error("Format gambar tidak didukung. Gunakan JPG, PNG, atau WebP.");
    }

    if (file.size > 10 * 1024 * 1024) {
      return toast.error("Ukuran foto gedung melebihi batas 10MB.");
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload-building-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        setBuildingPhotoUrl(data.url);
        toast.success("Foto gedung berhasil diunggah.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal mengunggah foto gedung.");
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handleRemoveBuildingPhoto = () => {
    setBuildingPhotoUrl("");
    toast.info("Foto gedung dihapus.");
  };

  const submit = async () => {
    if (!cust.name.trim()) return toast.error(t("err.nama"));
    const phone = normalizePhone(cust.phone);
    if (!validIntlPhone(phone)) return toast.error(t("err.phone"));

    let normEgyptPhone = null;
    if (cust.phone_number && cust.phone_number.trim()) {
      normEgyptPhone = normalizeEgyptPhone(cust.phone_number);
      if (!validEgyptPhone(normEgyptPhone)) {
        return toast.error("Nomor telepon harus nomor Mesir dengan kode negara +20.");
      }
    }

    if (!cust.address.trim()) return toast.error(t("err.alamat"));
    if (!delivery.method) return toast.error(t("err.metode"));
    if (delivery.method === "delivery" && !delivery.zone_id) return toast.error(t("err.zona"));
    const GOOGLE_MAPS_REGEX = /^https?:\/\/(?:(?:www\.)?google\.[a-z.]+\/maps(?:[/?#]|$)|maps\.google\.[a-z.]+(?:[/?#]|$)|maps\.app\.goo\.gl\/|goo\.gl\/maps\/)/i;
    let normalizedMaps = (cust.maps || "").trim();
    if (normalizedMaps && !/^https?:\/\//i.test(normalizedMaps)) {
      normalizedMaps = `https://${normalizedMaps}`;
    }

    if (delivery.method === "delivery") {
      if (!normalizedMaps) {
        return toast.error("Link Google Maps lokasi pengiriman wajib diisi untuk pesanan delivery.");
      }
      if (!GOOGLE_MAPS_REGEX.test(normalizedMaps)) {
        return toast.error("Link Maps harus berupa link Google Maps yang valid.");
      }
    } else if (normalizedMaps && !GOOGLE_MAPS_REGEX.test(normalizedMaps)) {
      return toast.error("Link Maps harus berupa link Google Maps yang valid.");
    }

    if (uploadingPhoto) {
      return toast.warning("Mohon tunggu hingga proses unggah foto gedung selesai.");
    }

    setSubmitting(true);
    const notes = [cust.notes, ...items.filter((i) => i.config?.custom_size && i.config?.custom_note).map((i) => `Custom (${i.product?.name || i.name}): ${i.config.custom_note}`)].filter(Boolean).join(" | ");
    try {
      const orderPayload = {
        customer_name: cust.name, customer_phone: phone, phone_number: normEgyptPhone, customer_address: cust.address,
        customer_maps_url: normalizedMaps, delivery_method: delivery.method,
        delivery_zone_id: delivery.method === "delivery" ? delivery.zone_id : null,
        building_photo_url: buildingPhotoUrl || null,
        payment_method: cust.payment, notes,
        discount_code: !ref.applied && disc.applied ? disc.code : null,
        referral_code: ref.applied ? ref.code : null,
        redeem_points: usedPts,
        items: items.map((i) => ({ product_id: i.product?.id || i.product_id, config: i.config, quantity: i.quantity })),
      };
      if (isInvoiceCart && activeInvoice?.invoice_id) {
        orderPayload.invoice_id = activeInvoice.invoice_id;
      }
      const { data } = await api.post("/orders", orderPayload);
      clear();
      navigate(`/pesanan/${data.id}`, { state: { order: data } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("err.gagal"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 lg:pb-10">
      <button onClick={() => navigate("/keranjang")} className="mb-4 inline-flex items-center gap-1 text-sm text-[#8B5A2B]" data-testid="checkout-back"><ChevronLeft size={16} /> {t("cart.title")}</button>
      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          {/* Delivery */}
          <div data-testid="checkout-delivery">
            <h2 className="font-heading text-xl font-bold text-[#2C1E16]">{t("cart.metode")}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <button onClick={() => setDelivery({ method: "pickup", zone_id: "" })} data-testid="delivery-pickup" className={`rounded-2xl border p-5 text-left ${delivery.method === "pickup" ? "border-[#8B5A2B] bg-[#EFE6D5]" : "border-[#E5DCC5] bg-white hover:border-[#8B5A2B]"}`}>
                <Store className="text-[#8B5A2B]" size={24} /><div className="mt-2 font-heading font-semibold text-[#2C1E16]">{t("del.ambil")}</div><div className="text-sm text-[#5C4A3D]">{t("del.ambil_d")}</div>
              </button>
              <button onClick={() => setDelivery({ method: "delivery", zone_id: "" })} data-testid="delivery-delivery" className={`rounded-2xl border p-5 text-left ${delivery.method === "delivery" ? "border-[#8B5A2B] bg-[#EFE6D5]" : "border-[#E5DCC5] bg-white hover:border-[#8B5A2B]"}`}>
                <Truck className="text-[#8B5A2B]" size={24} /><div className="mt-2 font-heading font-semibold text-[#2C1E16]">{t("del.kirim")}</div><div className="text-sm text-[#5C4A3D]">{t("del.kirim_d")}</div>
              </button>
            </div>
            {delivery.method === "pickup" && store?.store_address && (
              <div className="mt-3 rounded-2xl border border-[#E5DCC5] bg-white p-4 text-sm text-[#5C4A3D]"><div className="font-medium text-[#2C1E16]">{t("del.alamat_toko")}</div><p className="mt-1">{store.store_address}</p></div>
            )}
            {delivery.method === "delivery" && (
              <div className="mt-3">
                <Label className="mb-2 block font-heading text-sm font-semibold text-[#2C1E16]">{t("del.pilih_zona")}</Label>
                <div className="flex flex-wrap gap-2">{zones.map((z) => <Chip key={z.id} active={delivery.zone_id === z.id} onClick={() => setDelivery((d) => ({ ...d, zone_id: z.id }))} testid={`zone-${z.id}`}>{z.name} · {fmtLE(z.fee_le)} LE</Chip>)}</div>
              </div>
            )}
          </div>

          {/* Customer info */}
          <div className="space-y-4" data-testid="checkout-customer">
            <h2 className="font-heading text-xl font-bold text-[#2C1E16]">{t("chk.title")}</h2>
            <div><Label className="mb-1.5 block text-sm">{t("chk.nama")} *</Label><Input value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} data-testid="input-name" className="h-12 bg-white" /></div>
            <div>
              <Label className="mb-1.5 block text-sm">{t("chk.no_hp")} * <span className="text-xs text-[#8B7355]">(+kode negara)</span></Label>
              <Input value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="input-phone" placeholder={t("chk.phone_ph")} className="h-12 bg-white" />
              <p className="mt-1 text-xs text-[#8B7355]">{t("chk.phone_help")}</p>
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label className="text-sm font-medium text-[#2C1E16]">Nomor Telepon</Label>
                <span className="text-xs text-[#8B7355]">(Opsional — khusus nomor Mesir)</span>
              </div>
              <div className="relative flex items-center">
                <span className="absolute left-3 font-medium text-sm text-[#8B7355] select-none">+20</span>
                <Input
                  type="tel"
                  value={cust.phone_number}
                  onChange={(e) => setCust({ ...cust, phone_number: e.target.value })}
                  data-testid="input-phone-number"
                  placeholder="1x xxxx xxxx"
                  className="h-12 bg-white pl-12"
                />
              </div>
              <p className="mt-1 text-xs text-[#8B7355]">Nomor panggilan telepon lokal Mesir (opsional jika memiliki simcard Mesir)</p>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">{t("chk.alamat")} *</Label>
              <Textarea
                value={cust.address}
                onChange={(e) => setCust({ ...cust, address: e.target.value })}
                data-testid="input-address"
                placeholder={t("chk.alamat_ph")}
                className="min-h-[80px] bg-white text-sm"
              />
              <p className="mt-1 text-xs text-[#8B7355]">{t("chk.alamat_help")}</p>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">{t("chk.link_maps")} {delivery.method === "delivery" ? "* (Wajib untuk pengiriman kurir)" : t("chk.maps_opsional")}</Label>
              <Input
                value={cust.maps}
                onChange={(e) => setCust({ ...cust, maps: e.target.value })}
                data-testid="input-maps"
                placeholder="https://maps.app.goo.gl/... atau https://maps.google.com/..."
                className="h-12 bg-white"
              />
            </div>

            {/* Foto Gedung / Imarah (Opsional) */}
            <div className="rounded-xl border border-[#E5DCC5] bg-[#FAF8F5] p-3.5 space-y-2" data-testid="section-building-photo">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-[#2C1E16] flex items-center gap-1.5">
                  <Camera size={16} className="text-[#8B5A2B]" />
                  {t("chk.foto_gedung")}
                </Label>
                <span className="text-xs text-[#8B7355]">{t("chk.foto_gedung_opsional")}</span>
              </div>
              <p className="text-xs text-[#8B7355]">
                {t("chk.foto_gedung_help")}
              </p>

              {buildingPhotoUrl ? (
                <div className="flex items-center gap-3 pt-1">
                  <div className="h-16 w-16 overflow-hidden rounded-lg border border-[#E5DCC5] bg-white">
                    <img
                      src={imgUrl(buildingPhotoUrl)}
                      alt="Foto Gedung"
                      className="h-full w-full object-cover"
                      data-testid="building-photo-preview"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                      <CheckCircle2 size={14} /> Foto gedung tersimpan
                    </span>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveBuildingPhoto}
                        className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                        data-testid="btn-remove-building-photo"
                      >
                        <Trash2 size={12} className="mr-1" />
                        {t("chk.hapus_foto")}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="pt-1">
                  <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#8B5A2B]/40 bg-white px-4 py-3 text-xs font-semibold text-[#8B5A2B] hover:bg-[#FAF5EE] cursor-pointer transition-colors">
                    {uploadingPhoto ? (
                      <span className="flex items-center gap-2">
                        <Upload size={14} className="animate-spin" /> Mengunggah foto...
                      </span>
                    ) : (
                      <>
                        <Upload size={14} />
                        <span>{t("chk.unggah_foto")}</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleBuildingPhotoUpload}
                      disabled={uploadingPhoto || submitting}
                      data-testid="input-building-photo"
                    />
                  </label>
                  <p className="mt-1 text-[11px] text-[#8B7355]">Format: JPG, PNG, WebP (maks. 10MB)</p>
                </div>
              )}
            </div>
            <div>
              <Label className="mb-2 block font-heading text-sm font-semibold text-[#2C1E16]">{t("chk.metode_bayar")} *</Label>
              <div className="flex flex-wrap gap-2"><Chip active={cust.payment === "cash"} onClick={() => setCust({ ...cust, payment: "cash" })} testid="pay-cash">{t("chk.cash")}</Chip><Chip active={cust.payment === "transfer"} onClick={() => setCust({ ...cust, payment: "transfer" })} testid="pay-transfer">{t("chk.transfer")}</Chip></div>
              {cust.payment === "transfer" && <p className="mt-2 text-xs text-[#5C4A3D]">{t("chk.transfer_note")}</p>}
            </div>
            <div><Label className="mb-1.5 block text-sm">{t("chk.catatan")}</Label><Textarea value={cust.notes} onChange={(e) => setCust({ ...cust, notes: e.target.value })} data-testid="input-notes" className="min-h-[70px] bg-white" /></div>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 lg:h-fit">
          <div className="rounded-2xl border border-[#E5DCC5] bg-white p-5 shadow-sm" data-testid="checkout-summary">
            <h3 className="font-heading text-lg font-bold text-[#2C1E16]">{t("chk.review")}</h3>
            <div className="mt-3 space-y-2">
              {items.map((it) => (
                <div key={it.cartId} className="flex justify-between gap-3 text-sm">
                  <span className="text-[#5C4A3D]">{it.product?.name || it.name} <span className="text-[#8B7355]">×{it.quantity}</span><br /><span className="text-xs text-[#8B7355]">{config_summary_client(it.product?.category, it.config, t)}</span></span>
                  <span className="font-medium text-[#2C1E16]">{it.breakdown?.requiresConfirm ? "—" : `${fmtLE(it.breakdown?.subtotal || 0)} LE`}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-dashed border-[#E5DCC5] pt-2 text-sm"><span className="text-[#5C4A3D]">{t("sum.subtotal")}</span><span className="font-medium">{fmtLE(productSubtotal)} LE</span></div>
              {invoiceDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-700 font-medium">
                  <span>Diskon Invoice ({activeInvoice?.invoice_number})</span>
                  <span>-{fmtLE(invoiceDiscount)} LE</span>
                </div>
              )}
              {disc.applied && <div className="flex justify-between text-sm text-[#738678]"><span>{t("sum.discount_line")} ({disc.code})</span><span className="font-medium">-{fmtLE(disc.amount)} LE</span></div>}
              <div className="flex justify-between text-sm"><span className="text-[#5C4A3D]">{t("sum.pengiriman")}</span><span className="font-medium">{fmtLE(deliveryFee)} LE</span></div>
            </div>
            <div className="mt-3 flex gap-2">
              <Input
                value={disc.code}
                onChange={(e) => setDisc({ ...disc, code: e.target.value, applied: false })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyDiscount();
                  }
                }}
                placeholder={t("sum.discount_ph")}
                data-testid="discount-code"
                className="h-10 bg-white"
              />
              <Button
                onClick={() => applyDiscount()}
                disabled={validatingPromo}
                data-testid="apply-discount"
                variant="outline"
                className="h-10 rounded-xl border-[#8B5A2B] text-[#8B5A2B]"
              >
                {validatingPromo ? "..." : t("sum.discount_apply")}
              </Button>
            </div>
            {ref.applied && <div className="mt-2 flex justify-between text-sm text-[#738678]"><span>Referral ({ref.code})</span><span className="font-medium">-{fmtLE(ref.amount)} LE</span></div>}
            <div className="mt-2 flex gap-2">
              <Input
                value={ref.code}
                onChange={(e) => setRef({ ...ref, code: e.target.value, applied: false })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyReferral();
                  }
                }}
                placeholder="Kode referral"
                data-testid="referral-code"
                className="h-10 bg-white"
              />
              <Button
                onClick={() => applyReferral()}
                disabled={validatingReferral}
                data-testid="apply-referral"
                variant="outline"
                className="h-10 rounded-xl border-[#8B5A2B] text-[#8B5A2B]"
              >
                {validatingReferral ? "..." : "Pakai"}
              </Button>
            </div>
            {customer && maxPts > 0 && (
              <div className="mt-2">
                <div className="mb-1 flex justify-between text-xs text-[#8B7355]"><span>Tukar Poin (maks {fmtLE(maxPts)})</span>{usedPts > 0 && <span className="text-[#738678]">-{fmtLE(usedPts)} LE</span>}</div>
                <Input type="number" value={pts} onChange={(e) => setPts(e.target.value)} placeholder="0" data-testid="redeem-points" className="h-10 bg-white" />
              </div>
            )}
            <div className="mt-4 rounded-xl bg-[#EFE6D5] p-4">
              <div className="text-xs font-medium text-[#8B6B45]">{t("sum.estimasi_total")}</div>
              <div className="font-heading text-2xl font-bold text-[#8B5A2B]" data-testid="checkout-total-le">{fmtLE(totalLE)} LE</div>
              <div className="text-sm text-[#5C4A3D]" data-testid="checkout-total-idr">≈ {fmtIDR(totalLE * rate)}</div>
              <div className="text-[11px] text-[#8B7355]">{t("sum.rate_label")} Rp{fmtLE(rate)}/LE</div>
            </div>
            <p className="mt-3 text-[11px] text-[#8B7355]">{t("chk.disclaimer")}</p>
            <Button onClick={submit} disabled={submitting} data-testid="submit-order" className="mt-4 hidden h-12 w-full rounded-full bg-[#25D366] hover:bg-[#1eb556] lg:flex">{submitting ? "..." : t("btn.submit")}</Button>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 z-40 w-full border-t border-[#E5DCC5] bg-white/95 p-3 backdrop-blur-md lg:hidden">
        <Button onClick={submit} disabled={submitting} data-testid="submit-order-mobile" className="h-12 w-full rounded-full bg-[#25D366] hover:bg-[#1eb556]">{submitting ? "..." : `${t("btn.submit")} · ${fmtLE(totalLE)} LE`}</Button>
      </div>
    </div>
  );
}
